import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';

import { getDealInsights } from '../../services/api';
import { DealInsightsDto } from '../../types/types';
import { EmptyState } from './emptyState';
import { Skeleton } from './skeleton';

type InsightStatus = 'ACTIVE' | 'CLOSED';

const TABS: { value: InsightStatus; labelKey: string }[] = [
  { value: 'ACTIVE', labelKey: 'active_deal' },
  { value: 'CLOSED', labelKey: 'closed_deal' },
];

const INSIGHT_CARDS: {
  titleKey: string;
  value: (insights: DealInsightsDto) => number;
  cardClass: string;
}[] = [
  { titleKey: 'total_refund', value: (insights) => insights.totalRefund, cardClass: 'bg-blue-50' },
  { titleKey: 'total_expense', value: (insights) => insights.totalExpense, cardClass: 'bg-orange-50' },
  { titleKey: 'total_deals', value: (insights) => insights.totalDeals, cardClass: 'bg-green-50' },
];

export const InsightsCard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const [status, setStatus] = useState<InsightStatus>('CLOSED');
  const [insights, setInsights] = useState<DealInsightsDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);

    getDealInsights(status)
      .then((data) => {
        if (!isCancelled) setInsights(data);
      })
      .catch(() => {
        if (!isCancelled) {
          setInsights(null);
          toast.error(t('failed_to_load_insights'));
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [status, t]);

  const isEmpty = !isLoading && (!insights || insights.totalDeals === 0);

  return (
    <div className="flex h-full min-w-0 flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
      <p className="text-sm font-medium leading-5 text-gray-900">{t('insights')}</p>

      <div className="inline-flex w-fit items-center gap-0.5 rounded-3xl bg-gray-100 px-2 py-1">
        {TABS.map(({ value, labelKey }) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            className={`rounded-3xl px-3 py-1.5 text-sm font-medium leading-5 transition-colors ${
              status === value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      {isLoading && (
        <div role="status" aria-busy="true" className="flex flex-1 flex-col gap-4">
          {INSIGHT_CARDS.map(({ titleKey, cardClass }) => (
            <div key={titleKey} className={`flex min-h-[110px] flex-1 flex-col justify-between gap-4 rounded-3xl p-4 transition-colors ${cardClass}`}>
              <Skeleton className="h-4 w-24 bg-white/70" />
              <Skeleton className="h-10 w-16 bg-white/70" />
            </div>
          ))}
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState text={t('no_data_to_display')} />
        </div>
      )}

      {!isLoading && insights && !isEmpty && (
        <div className="flex flex-1 flex-col gap-4">
          {INSIGHT_CARDS.map(({ titleKey, value, cardClass }) => (
            <div key={titleKey} className={`flex min-h-[110px] flex-1 flex-col justify-between gap-4 rounded-3xl p-4 transition-colors ${cardClass}`}>
              <p className="text-sm font-medium leading-5 text-gray-900">{t(titleKey)}</p>
              <p className="text-4xl font-extrabold leading-10 text-gray-900">{value(insights)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
